/**
 * usePageModules Hook
 *
 * Manages which pages/modules are enabled or disabled in the application.
 * Allows users to customize their sidebar by enabling only the features they use.
 * Uses React Context to share state across all components.
 */

import { useState, useEffect, useCallback, createContext, useContext, useRef, type ReactNode } from 'react';
import { StorageService } from '../services/storage';
import { scheduleDeviceSync } from '../services/deviceSyncService';
import { supabase } from '../lib/supabase/client';
import { useAuthStore } from '../store/authStore';

// Module definition
export interface PageModule {
  id: string;
  label: string;
  description: string;
  category: ModuleCategory;
  icon: string; // Icon name from Icons component
  defaultEnabled: boolean;
  alwaysVisible?: boolean; // Cannot be disabled (Portfolio, Settings, SeedVault)
  expertOnly?: boolean; // Only visible in expert mode
  linkedPages?: string[]; // Other page IDs that are enabled when this module is enabled
  requiresAuth?: boolean; // Requires user to be logged in
}

export type ModuleCategory =
  | 'core'
  | 'bitcoin'
  | 'analytics'
  | 'monitoring'
  | 'exchanges'
  | 'tools'
  | 'community'
  | 'advanced';

// All available modules
export const PAGE_MODULES: PageModule[] = [
  // Core - Always visible
  {
    id: 'module-hub',
    label: 'Module Hub',
    description: 'Centre de contrôle pour activer/désactiver les fonctionnalités',
    category: 'core',
    icon: 'Layout',
    defaultEnabled: true,
    alwaysVisible: true,
  },
  {
    id: 'portfolio',
    label: 'Portfolio',
    description: 'Gérez vos cryptomonnaies et suivez leur performance',
    category: 'core',
    icon: 'Briefcase',
    defaultEnabled: true,
    alwaysVisible: true,
  },
  {
    id: 'seed-vault',
    label: 'SeedCrypt',
    description: 'Cryptage & Stéganographie d\'une partie de vos seed phrases',
    category: 'tools',
    icon: 'Shield',
    defaultEnabled: false,
  },
  {
    id: 'settings',
    label: 'Settings',
    description: 'Paramètres de l\'application',
    category: 'core',
    icon: 'Settings',
    defaultEnabled: true,
    alwaysVisible: true,
  },
  {
    id: 'pricing',
    label: 'Tarifs',
    description: 'Découvrez nos offres et abonnements',
    category: 'core',
    icon: 'CreditCard',
    defaultEnabled: true,
    alwaysVisible: true,
  },

  // Analytics
  {
    id: 'analytics',
    label: 'Analytics',
    description: 'Analyses avancées de votre portfolio (Vue d\'ensemble, Performance, Risque, Trading, Positions)',
    category: 'analytics',
    icon: 'BarChart',
    defaultEnabled: true,
  },
  {
    id: 'swap-analytics',
    label: 'Swap Analytics',
    description: 'Statistiques, historique et export des opérations de swap',
    category: 'analytics',
    icon: 'ArrowRightLeft',
    defaultEnabled: true,
    alwaysVisible: true,
  },
  // V2.G5: visualizations 1/2/3 modules retired — charts canonicalized into analytics/*.
  // Bitcoin
  {
    id: 'bitcoin-cycles',
    label: 'Cycles Bitcoin',
    description: 'Analyse des cycles historiques du Bitcoin',
    category: 'bitcoin',
    icon: 'TrendingUp',
    defaultEnabled: true,
  },
  {
    id: 'bitcoin-retirement',
    label: 'Retraite Bitcoin',
    description: 'Calculateur de retraite basé sur vos holdings Bitcoin',
    category: 'bitcoin',
    icon: 'Target',
    defaultEnabled: true,
  },

  // Monitoring
  {
    id: 'dashboard-ipad',
    label: 'Dashboard iPad',
    description: 'Watchlist épurée avec alertes visuelles style TickrMeter',
    category: 'monitoring',
    icon: 'Monitor',
    defaultEnabled: false,
  },
  {
    id: 'fluxnode-monitor',
    label: 'FluxNode Monitor',
    description: 'Surveillez vos nodes Flux et leurs récompenses',
    category: 'monitoring',
    icon: 'Flux',
    defaultEnabled: true,
  },
  {
    id: 'hedera-monitor',
    label: 'Hedera Monitor',
    description: 'Suivez vos comptes Hedera, staking et rewards',
    category: 'monitoring',
    icon: 'Hedera',
    defaultEnabled: true,
  },
  {
    id: 'cronos-monitor',
    label: 'Cronos Monitor',
    description: 'Suivez votre staking CRO, rewards et validators sur Cronos POS Chain',
    category: 'monitoring',
    icon: 'Cronos',
    defaultEnabled: true,
  },
  {
    id: 'thegraph-monitor',
    label: 'The Graph Monitor',
    description: 'Suivez vos délégations GRT, rewards et indexers sur The Graph Network',
    category: 'monitoring',
    icon: 'TheGraph',
    defaultEnabled: true,
  },
  {
    id: 'portfolio-onchain',
    label: 'Portfolio On-Chain ERC-20',
    description: 'Visualisez vos actifs on-chain via DeBank (tokens, DeFi, historique)',
    category: 'monitoring',
    icon: 'Link',
    defaultEnabled: true,
    requiresAuth: true,
  },
  {
    id: 'wallet-tracker',
    label: 'Wallet Tracker',
    description: 'Suivez des wallets externes (EVM)',
    category: 'monitoring',
    icon: 'Wallet',
    defaultEnabled: false,
    requiresAuth: true,
  },
  {
    id: 'congress-trading',
    label: 'Congress Trading',
    description: 'Suivez les transactions boursières des membres du Congrès US (STOCK Act)',
    category: 'monitoring',
    icon: 'Briefcase',
    defaultEnabled: true,
    requiresAuth: true,
  },
  {
    id: 'algorand-monitor',
    label: 'Algorand Monitor',
    description: 'Suivez vos comptes Algorand, ASA tokens et rewards',
    category: 'monitoring',
    icon: 'Algorand',
    defaultEnabled: false,
  },
  {
    id: 'cardano-monitor',
    label: 'Cardano Monitor',
    description: 'Suivez vos comptes Cardano, staking ADA et rewards',
    category: 'monitoring',
    icon: 'Cardano',
    defaultEnabled: false,
  },
  {
    id: 'xdc-monitor',
    label: 'XDC Monitor',
    description: 'Suivez vos comptes XDC Network et masternodes',
    category: 'monitoring',
    icon: 'Xdc',
    defaultEnabled: false,
  },
  {
    id: 'vechain-monitor',
    label: 'VeChain Monitor',
    description: 'Suivez vos comptes VeChain, génération VTHO et nodes',
    category: 'monitoring',
    icon: 'Vechain',
    defaultEnabled: false,
  },
  {
    id: 'icp-monitor',
    label: 'ICP Monitor',
    description: 'Suivez vos comptes Internet Computer, neurons et staking',
    category: 'monitoring',
    icon: 'Icp',
    defaultEnabled: false,
  },
  {
    id: 'axelar-monitor',
    label: 'Axelar Monitor',
    description: 'Suivez vos comptes Axelar, staking AXL et cross-chain activity',
    category: 'monitoring',
    icon: 'Axelar',
    defaultEnabled: false,
  },

  // Exchanges
  {
    id: 'cryptocom-dashboard',
    label: 'Crypto.com Dashboard',
    description: 'Import et analyse de vos données Crypto.com',
    category: 'exchanges',
    icon: 'CryptoCom',
    defaultEnabled: false,
  },
  {
    id: 'kucoin-dashboard',
    label: 'KuCoin Dashboard',
    description: 'Import et analyse de vos données KuCoin',
    category: 'exchanges',
    icon: 'KuCoin',
    defaultEnabled: false,
  },

  // Tools
  // {
  //   id: 'academy',
  //   label: 'Academy',
  //   description: 'Apprenez les bases de la crypto et du trading',
  //   category: 'tools',
  //   icon: 'BookOpen',
  //   defaultEnabled: false,
  // },
  {
    id: 'crypto-quiz',
    label: 'Quiz Crypto',
    description: 'Testez vos connaissances crypto avec des quiz interactifs',
    category: 'tools',
    icon: 'Brain',
    defaultEnabled: false,
  },
  {
    id: 'guide',
    label: 'Guide Complet',
    description: 'Documentation complète de l\'application',
    category: 'tools',
    icon: 'FileText',
    defaultEnabled: true,
  },
  {
    id: 'tax-center',
    label: 'Centre Fiscal',
    description: 'Guide fiscal, calcul des plus-values (Art. 150 VH bis), Tax Loss Harvesting et génération formulaires 2086/3916-bis',
    category: 'tools',
    icon: 'Calculator',
    defaultEnabled: false,
    requiresAuth: true,
    // This module controls access to: tax-strategies, tax-center, tax-loss-harvesting pages
    linkedPages: ['tax-strategies', 'tax-loss-harvesting'],
  },
  {
    id: 'changelog',
    label: 'Changelog',
    description: 'Historique des mises à jour',
    category: 'tools',
    icon: 'FileText',
    defaultEnabled: false,
  },

  // Community
  {
    id: 'projects',
    label: 'Projets',
    description: 'Découvrez les projets communautaires, votez et soutenez le développement',
    category: 'community',
    icon: 'Rocket',
    defaultEnabled: true,
  },

  // Advanced (expert only)
  {
    id: 'bot',
    label: 'AI Trading Bot',
    description: 'Bot de trading simulé avec intelligence artificielle',
    category: 'advanced',
    icon: 'Bot',
    defaultEnabled: false,
    expertOnly: true,
  },
  {
    id: 'autopilot',
    label: 'Autopilot',
    description: 'Gestion automatisée du portfolio',
    category: 'advanced',
    icon: 'Sparkles',
    defaultEnabled: false,
    expertOnly: true,
  },
  {
    id: 'portfolio-rebalancing',
    label: 'Portfolio Rebalancing',
    description: 'Rééquilibrage automatique du portfolio',
    category: 'advanced',
    icon: 'Target',
    defaultEnabled: false,
    expertOnly: true,
  },
  // V2.G6: advanced-analytics module retired (page fused into analytics/Insights).
  {
    id: 'logs',
    label: 'Logs',
    description: 'Journaux de l\'application pour le débogage',
    category: 'advanced',
    icon: 'FileText',
    defaultEnabled: false,
    expertOnly: true,
  },
];

// Category labels
export const CATEGORY_LABELS: Record<ModuleCategory, string> = {
  core: 'Essentiel',
  bitcoin: 'Bitcoin',
  analytics: 'Analytics',
  monitoring: 'Monitoring',
  exchanges: 'Exchanges',
  tools: 'Outils',
  community: 'Communauté',
  advanced: 'Avancé',
};

// Category descriptions
export const CATEGORY_DESCRIPTIONS: Record<ModuleCategory, string> = {
  core: 'Fonctionnalités essentielles toujours actives',
  bitcoin: 'Outils d\'analyse et de planification Bitcoin',
  analytics: 'Analyses et visualisations de données',
  monitoring: 'Surveillance de wallets et nodes externes',
  exchanges: 'Intégration avec les exchanges',
  tools: 'Outils éducatifs et documentation',
  community: 'Projets communautaires et collaboratifs',
  advanced: 'Fonctionnalités avancées pour experts',
};

// Storage key
const STORAGE_KEY = 'page_modules_enabled';

// Type for enabled modules state
export type EnabledModules = Record<string, boolean>;

/**
 * Get default enabled state for all modules
 */
export const getDefaultEnabledModules = (): EnabledModules => {
  const defaults: EnabledModules = {};
  for (const module of PAGE_MODULES) {
    defaults[module.id] = module.defaultEnabled || module.alwaysVisible || false;
  }
  return defaults;
};

// Context type
interface PageModulesContextType {
  enabledModules: EnabledModules;
  setEnabledModules: React.Dispatch<React.SetStateAction<EnabledModules>>;
}

// Create context with undefined default (will be set by provider)
const PageModulesContext = createContext<PageModulesContextType | undefined>(undefined);

/**
 * Provider component that wraps the app and provides shared module state
 * Syncs with Supabase when user is logged in
 */
export function PageModulesProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuthStore();
  const [enabledModules, setEnabledModules] = useState<EnabledModules>(() => {
    const saved = StorageService.get<EnabledModules>(STORAGE_KEY);
    if (saved) {
      // Merge with defaults to handle new modules
      const defaults = getDefaultEnabledModules();
      return { ...defaults, ...saved };
    }
    return getDefaultEnabledModules();
  });
  const [isLoadedFromSupabase, setIsLoadedFromSupabase] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load from Supabase when user logs in
  useEffect(() => {
    const loadFromSupabase = async () => {
      if (!isAuthenticated || !user?.id) {
        setIsLoadedFromSupabase(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('settings')
          .eq('id', user.id)
          .single();

        if (error) {
          console.warn('[PageModules] Failed to load from Supabase:', error.message);
          return;
        }

        if (data?.settings && typeof data.settings === 'object') {
          const settings = data.settings as Record<string, unknown>;
          if (settings.enabled_modules && typeof settings.enabled_modules === 'object') {
            const cloudModules = settings.enabled_modules as EnabledModules;
            const defaults = getDefaultEnabledModules();
            const merged = { ...defaults, ...cloudModules };
            setEnabledModules(merged);
            // Also update localStorage
            StorageService.set(STORAGE_KEY, merged);
            console.log('[PageModules] Loaded from Supabase');
          }
        }
        setIsLoadedFromSupabase(true);
      } catch (e) {
        console.warn('[PageModules] Error loading from Supabase:', e);
      }
    };

    loadFromSupabase();
  }, [isAuthenticated, user?.id]);

  // Save to Supabase when modules change (debounced)
  useEffect(() => {
    // Always save to localStorage immediately
    StorageService.set(STORAGE_KEY, enabledModules);

    // V2.N.4 — mirror enabled modules to DB (debounced), even for
    // anonymous users (the device sync endpoint handles no-account).
    scheduleDeviceSync();

    // Only save to Supabase if user is logged in and we've loaded from Supabase first
    if (!isAuthenticated || !user?.id || !isLoadedFromSupabase) {
      return;
    }

    // Debounce saves to Supabase
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      try {
        // Get current settings first
        const { data: currentData } = await supabase
          .from('user_profiles')
          .select('settings')
          .eq('id', user.id)
          .single();

        const currentSettings = (currentData?.settings as Record<string, unknown>) || {};

        // Merge with existing settings
        const newSettings = {
          ...currentSettings,
          enabled_modules: enabledModules,
        };

        const { error } = await supabase
          .from('user_profiles')
          .update({ settings: newSettings, updated_at: new Date().toISOString() })
          .eq('id', user.id);

        if (error) {
          console.warn('[PageModules] Failed to save to Supabase:', error.message);
        } else {
          console.log('[PageModules] Saved to Supabase');
        }
      } catch (e) {
        console.warn('[PageModules] Error saving to Supabase:', e);
      }
    }, 1000); // 1 second debounce

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [enabledModules, isAuthenticated, user?.id, isLoadedFromSupabase]);

  return (
    <PageModulesContext.Provider value={{ enabledModules, setEnabledModules }}>
      {children}
    </PageModulesContext.Provider>
  );
}

/**
 * Hook to manage page modules - uses shared context state
 */
export const usePageModules = () => {
  const context = useContext(PageModulesContext);

  // Fallback for when used outside provider (backward compatibility)
  const [localEnabledModules, setLocalEnabledModules] = useState<EnabledModules>(() => {
    const saved = StorageService.get<EnabledModules>(STORAGE_KEY);
    if (saved) {
      const defaults = getDefaultEnabledModules();
      return { ...defaults, ...saved };
    }
    return getDefaultEnabledModules();
  });

  // Use context if available, otherwise use local state
  const enabledModules = context?.enabledModules ?? localEnabledModules;
  const setEnabledModules = context?.setEnabledModules ?? setLocalEnabledModules;

  // Save to storage when changed (only for fallback mode, context handles its own)
  useEffect(() => {
    if (!context) {
      StorageService.set(STORAGE_KEY, enabledModules);
    }
  }, [enabledModules, context]);

  // Check if a module is enabled
  const isModuleEnabled = useCallback((moduleId: string): boolean => {
    const module = PAGE_MODULES.find(m => m.id === moduleId);
    if (module?.alwaysVisible) return true;
    if (enabledModules[moduleId]) return true;

    // Check if this page is a linkedPage of another enabled module
    for (const mod of PAGE_MODULES) {
      if (mod.linkedPages?.includes(moduleId) && enabledModules[mod.id]) {
        return true;
      }
    }

    return false;
  }, [enabledModules]);

  // Toggle a module
  const toggleModule = useCallback((moduleId: string) => {
    const module = PAGE_MODULES.find(m => m.id === moduleId);
    if (module?.alwaysVisible) return; // Cannot toggle always-visible modules

    setEnabledModules(prev => {
      const newEnabled = !prev[moduleId];
      // Track module toggle in analytics
      if (module) {
      }
      return {
        ...prev,
        [moduleId]: newEnabled
      };
    });
  }, []);

  // Enable a module
  const enableModule = useCallback((moduleId: string) => {
    setEnabledModules(prev => ({
      ...prev,
      [moduleId]: true
    }));
  }, []);

  // Disable a module
  const disableModule = useCallback((moduleId: string) => {
    const module = PAGE_MODULES.find(m => m.id === moduleId);
    if (module?.alwaysVisible) return;

    setEnabledModules(prev => ({
      ...prev,
      [moduleId]: false
    }));
  }, []);

  // Enable all modules in a category
  const enableCategory = useCallback((category: ModuleCategory) => {
    const modulesInCategory = PAGE_MODULES.filter(m => m.category === category);
    setEnabledModules(prev => {
      const updated = { ...prev };
      for (const module of modulesInCategory) {
        updated[module.id] = true;
      }
      return updated;
    });
    // Track category enable in analytics
  }, []);

  // Disable all modules in a category (except always-visible)
  const disableCategory = useCallback((category: ModuleCategory) => {
    const modulesInCategory = PAGE_MODULES.filter(m => m.category === category && !m.alwaysVisible);
    setEnabledModules(prev => {
      const updated = { ...prev };
      for (const module of modulesInCategory) {
        updated[module.id] = false;
      }
      return updated;
    });
    // Track category disable in analytics
  }, []);

  // Reset to defaults
  const resetToDefaults = useCallback(() => {
    setEnabledModules(getDefaultEnabledModules());
    // Track reset in analytics
  }, []);

  // Get enabled modules for export
  const getEnabledModulesForExport = useCallback((): EnabledModules => {
    return { ...enabledModules };
  }, [enabledModules]);

  // Import modules settings
  const importModulesSettings = useCallback((settings: EnabledModules) => {
    const defaults = getDefaultEnabledModules();
    // Ensure always-visible modules are enabled
    const merged = { ...defaults, ...settings };
    for (const module of PAGE_MODULES) {
      if (module.alwaysVisible) {
        merged[module.id] = true;
      }
    }
    setEnabledModules(merged);
  }, []);

  // Get modules grouped by category
  const getModulesByCategory = useCallback(() => {
    const grouped: Record<ModuleCategory, PageModule[]> = {
      core: [],
      bitcoin: [],
      analytics: [],
      monitoring: [],
      exchanges: [],
      tools: [],
      community: [],
      advanced: [],
    };

    for (const module of PAGE_MODULES) {
      grouped[module.category].push(module);
    }

    return grouped;
  }, []);

  // Count enabled modules (excluding always-visible)
  const enabledCount = Object.entries(enabledModules)
    .filter(([id, enabled]) => {
      const module = PAGE_MODULES.find(m => m.id === id);
      return enabled && !module?.alwaysVisible;
    }).length;

  const totalOptionalModules = PAGE_MODULES.filter(m => !m.alwaysVisible).length;

  return {
    enabledModules,
    isModuleEnabled,
    toggleModule,
    enableModule,
    disableModule,
    enableCategory,
    disableCategory,
    resetToDefaults,
    getEnabledModulesForExport,
    importModulesSettings,
    getModulesByCategory,
    enabledCount,
    totalOptionalModules,
  };
};
